/**
 * Ingest project — the middle step of the line (build plan §3, Segment 2).
 *
 * The moment a software project (Artec Studio / RealityScan / Cyclone) gets
 * SAVED AND NAMED. Two plain ways in (Ivin, 2026-06-06):
 *   - FROM RAW — tick subjects whose raw is already ingested. Empty subjects
 *     (made, no data) are greyed — but you can ACTIVATE one right on its row as a
 *     Spider / Leo / NimbleTrack scan (tethered, no raw → it stops being Empty).
 *   - OR ADD A NEW SUBJECT — a brand-new tethered subject, born live.
 * …into a NEW project (a new subject makes the project), or ADD to an EXISTING one.
 *
 * A subject is ONE thing, unified across raw → project → export. Folder
 * organisation is deferred (Ivin: "for now all subjects in a job are in one
 * bucket") — we name, we don't nest. Ingest NEVER moves files.
 *
 * Ported faithfully from the locked mockup (components/handheldIngest/StartLive.jsx);
 * wired to the real backend. The link writes BOTH sides atomically; a mid-commit
 * failure rolls back (no orphan project, no one-sided links — build plan §9 R2).
 *
 * Minimal editorial copy (Ivin directive): labels speak, no paragraphs.
 */
import React, { useRef, useState } from "react";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { handheld, newClientTempId } from "../../../../lib/handheldCallables.js";
import { undoProjectCommit } from "../../../../lib/handheldRollback.js";
import {
  PPT_CATEGORIES, TETHERED, SOFTWARE_FOR_CODE, pad2, pad3, projectFileName,
} from "../../../../lib/handheldNaming.js";

const pptCode = pad3;
const projectCode = pad2;

// Empty = made, but no data yet (no raw, no project, no export).
const isEmpty = (s) => !(s.rawSets?.length) && !(s.projectIds?.length) && !(s.exports?.length);

function copyText(t, onDone) {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(t).then(onDone).catch(onDone);
  else onDone();
}

function CopyBtn({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className={`proc-copy ${done ? "done" : ""}`}
      onClick={() => copyText(text, () => { setDone(true); setTimeout(() => setDone(false), 1100); })}>
      {done ? "✓ copied" : label}
    </button>
  );
}

export default function IngestProject({ jobId, jobLabel, canErase = false, onDone }) {
  const { data: subjects } = useFirestoreCollection(`jobs/${jobId}/subjects`);
  const { data: projects } = useFirestoreCollection(`jobs/${jobId}/projects`);
  const nameStem = jobLabel ?? jobId; // human label for filenames/paths; doc id stays the key
  const projectsById = Object.fromEntries(projects.map((p) => [p.id, p]));
  const orderedProjects = [...projects].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
  const allSubjects = [...subjects].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  const [existingId, setExistingId] = useState("");   // optional: join an existing project (blank = new)
  const [picked, setPicked] = useState({});       // subjectId -> true (raw-ingested subjects ticked)
  const [activate, setActivate] = useState({});   // subjectId -> tethered code (an Empty activated on its row)
  const [born, setBorn] = useState([]);           // brand-new tethered subjects
  const [bornName, setBornName] = useState("");
  const [bornCat, setBornCat] = useState("Object");
  const [bornCode, setBornCode] = useState("sps");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const inFlightRef = useRef(false);
  const [result, setResult] = useState(null);

  // A new subject makes a NEW project; picking an existing one joins it. No "new project" box.
  const mode = existingId ? "existing" : "new";

  const isPicked = (id) => Boolean(picked[id]);
  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const setActivateCode = (id, code) => setActivate((a) => {
    const n = { ...a };
    if (code) n[id] = code; else delete n[id];
    return n;
  });

  const selectedIds = allSubjects.filter((s) => isPicked(s.id) && !isEmpty(s)).map((s) => s.id);
  const activateEntries = Object.entries(activate);
  const totalToAdd = selectedIds.length + activateEntries.length + born.length;

  const canCommit = totalToAdd > 0 && !busy;   // can't have a project without a subject
  // A new (born-in-software) subject takes the next number(s) — projected so the
  // project's serial + manifest reflect it the moment you add it.
  const nextNum = Math.max(0, ...allSubjects.map((s) => s.number ?? 0)) + 1;
  const bornNumbers = born.map((_, i) => nextNum + i);
  const nextProjectNumber = Math.max(0, ...projects.map((p) => p.number ?? 0)) + 1;
  // Project save-name = a SERIAL: the LOWEST subject number in the project (Ivin,
  // 2026-06-06). Every subject still rides in the manifest, attributed to the job.
  const involvedNumbers = (() => {
    const nums = [
      ...selectedIds.map((id) => allSubjects.find((s) => s.id === id)?.number),
      ...activateEntries.map(([id]) => allSubjects.find((s) => s.id === id)?.number),
      ...bornNumbers,
    ];
    if (mode === "existing" && existingId) {
      allSubjects.forEach((s) => { if ((s.projectIds ?? []).includes(existingId)) nums.push(s.number); });
    }
    return [...new Set(nums.filter((n) => Number.isFinite(n)))].sort((a, b) => a - b);
  })();
  // The filename a project would get — the OWNER (lowest project serial sharing this
  // lowest subject) keeps the clean base, later ones get _p<serial>. Same logic for the
  // pre-commit copy AND the committed result, so they always match.
  const nameFor = (low, serial, selfId) => {
    if (!Number.isFinite(low)) return "—";
    const base = `${nameStem}_${pptCode(low)}`;
    const sharerSerials = projects.filter((p) => p.id !== selfId).filter((p) => {
      const nums = subjects.filter((s) => (s.projectIds ?? []).includes(p.id)).map((s) => s.number).filter(Number.isFinite);
      return (nums.length ? Math.min(...nums) : (p.number ?? 0)) === low;
    }).map((p) => p.number ?? Infinity);
    if (!sharerSerials.length) return base;
    return serial === Math.min(serial, ...sharerSerials) ? base : `${base}_p${projectCode(serial)}`;
  };
  const saveSerial = !involvedNumbers.length ? "—"
    : nameFor(involvedNumbers[0], mode === "existing" ? projectsById[existingId]?.number : nextProjectNumber, mode === "existing" ? existingId : null);
  const locationPath = `…/${nameStem}/`;   // a real file path (root is a placeholder — Ivin owes it)
  // Software comes from the gear — the anchor (lowest) subject's tool.
  const anchorNum = involvedNumbers[0];
  const firstCode = allSubjects.find((s) => s.number === anchorNum)?.capturedBy?.[0]
    ?? born[bornNumbers.indexOf(anchorNum)]?.code
    ?? activateEntries[0]?.[1] ?? born[0]?.code ?? null;
  const software = mode === "existing"
    ? (projectsById[existingId]?.software ?? "Artec Studio")
    : (SOFTWARE_FOR_CODE[firstCode] ?? "Artec Studio");

  const addBorn = () => {
    const name = bornName.trim();
    if (!name) return;
    const taken = allSubjects.some((s) => s.displayName.toLowerCase() === name.toLowerCase())
      || born.some((b) => b.name.toLowerCase() === name.toLowerCase());
    if (taken) { setErr(`"${name}" is already used`); return; }
    setBorn((b) => [...b, { name, category: bornCat, code: bornCode }]);
    setBornName(""); setErr(null);
  };
  const removeBorn = (i) => setBorn((b) => b.filter((_, idx) => idx !== i));

  const commit = async () => {
    if (!canCommit) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true); setErr(null);

    // Track progress so a mid-commit failure can roll back (no orphan project,
    // no one-sided links). Each link writes both sides atomically server-side.
    let pid = null, pserial = null, createdId = null;
    const linked = [];          // links created this run; activated links clear their tethered code on rollback
    const createdSubjects = []; // born subjects created this run: { id, number }
    let pending = null;         // the link in flight (if its call fails, it may still have gone through)
    // Erasing is an admin's (Segment 5: it uses up the number). For anyone
    // else the links are still undone, and what this attempt made stays on
    // the list; the error says which (handheldRollback.js).
    let alreadyLinked = [];     // joining an existing project: its members before this attempt (never unlinked)
    const rollback = () => undoProjectCommit(handheld, {
      jobId, projectId: pid, linked, pending, alreadyLinked,
      created: { subjects: createdSubjects, project: createdId ? { id: createdId, number: pserial } : null },
      canErase,
    });

    try {
      if (mode === "existing") {
        pid = existingId; pserial = projectsById[existingId]?.number;
        alreadyLinked = allSubjects.filter((s) => (s.projectIds ?? []).includes(existingId)).map((s) => s.id);
      } else {
        const res = await handheld.createProject({
          jobId, displayName: "", software,
          tethered: selectedIds.length === 0,
          clientTempId: newClientTempId(),
        });
        pid = res.id; pserial = res.number; createdId = res.id;
      }

      for (const sid of selectedIds) {
        pending = { subjectId: sid };
        await handheld.linkSubjectToProject({ jobId, subjectId: sid, projectId: pid });
        linked.push({ subjectId: sid }); pending = null;
      }
      for (const [sid, code] of activateEntries) {
        pending = { subjectId: sid };
        await handheld.activateTethered({ jobId, subjectId: sid, code, projectId: pid });
        linked.push({ subjectId: sid, clearCapturedByCode: code }); pending = null;
      }
      for (const bn of born) {
        const r = await handheld.addSubject({
          jobId, displayName: bn.name, category: bn.category, capturedBy: [bn.code],
          clientTempId: newClientTempId(),
        });
        createdSubjects.push({ id: r.id, number: r.number });
        pending = { subjectId: r.id };
        await handheld.linkSubjectToProject({ jobId, subjectId: r.id, projectId: pid });
        linked.push({ subjectId: r.id }); pending = null;
      }

      // Final filename — same nameFor logic as the pre-commit copy, so they match.
      const resultName = nameFor(involvedNumbers[0], pserial, mode === "existing" ? pid : null);
      setBusy(false);
      inFlightRef.current = false;
      setResult({ name: resultName, count: totalToAdd, existing: mode === "existing", path: `${locationPath}${resultName}` });
    } catch (e) {
      const left = await rollback();
      setErr([e?.message ?? "something went wrong", left].filter(Boolean).join(" "));
      setBusy(false);
      inFlightRef.current = false;
    }
  };

  // ── done ──────────────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="raw-flow">
        <div className="raw-bar">
          <span className="raw-title">Project ingested</span>
          <button type="button" className="hh-btn primary" onClick={onDone}>Done</button>
        </div>
        <div className="raw-result">
          <span className="mono hh-file">{result.name}</span>
          <span className="proc-verified">✓ found in {result.path}</span>
          <span className="raw-verified">✓ {result.count} {result.count === 1 ? "subject" : "subjects"} {result.existing ? "added" : "in this project"}</span>
        </div>
      </div>
    );
  }

  // ── setup ─────────────────────────────────────────────────────────────────────
  return (
    <div className="raw-flow">
      <div className="raw-bar">
        <span className="raw-title">Ingest project</span>
        <button type="button" className="hh-btn ghost" onClick={onDone}>Cancel</button>
      </div>

      {/* a new subject (born in the software) — adding one is what makes a new project */}
      <div className="ip-section">
        <div className="ip-section-head">Add a new subject <span className="raw-hint">scanned in the software — Spider / Leo / NimbleTrack</span></div>
        <div className="sl-add">
          <select className="hh-cat" value={bornCode} onChange={(e) => setBornCode(e.target.value)}>
            {TETHERED.map((g) => <option key={g.code} value={g.code}>{g.code}</option>)}
          </select>
          <input className="hh-name-input" placeholder="subject name" value={bornName}
            onChange={(e) => setBornName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addBorn(); }} />
          <select className="hh-cat" value={bornCat} onChange={(e) => setBornCat(e.target.value)}>
            {PPT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="button" className="hh-btn ghost" disabled={!bornName.trim()} onClick={addBorn}>+ Add</button>
        </div>
      </div>

      {/* existing subjects with raw — pick them into the project too */}
      <div className="ip-section">
        <div className="ip-section-head">Subjects <span className="raw-hint">pick subjects with raw, or activate an Empty one — new ones land here too</span></div>
        {(allSubjects.length || born.length) ? (
          <div className="ip-rawlist">
            {born.map((b, i) => (
              <div key={`born-${i}`} className="ip-rawrow on ip-newrow">
                <input type="checkbox" checked readOnly />
                <span className="hh-ppt-num mono">{pptCode(bornNumbers[i])}</span>
                <span className="ip-rawname">{b.name}</span>
                <span className="hh-code mono">{b.code}</span>
                <span className="ip-new-tag">new</span>
                <button type="button" className="ip-born-x" title="Remove" onClick={() => removeBorn(i)}>×</button>
              </div>
            ))}
            {allSubjects.map((s) => {
              if (isEmpty(s)) {
                const code = activate[s.id];
                return (
                  <div key={s.id} className={`ip-emptyrow ${code ? "activating" : ""}`}>
                    <span className="hh-ppt-num mono">{pptCode(s.number)}</span>
                    <span className="ip-rawname">{s.displayName}</span>
                    <span className="ip-empty-right">
                      {code ? <span className="hh-code mono">{code}</span> : <span className="hh-empty-tag">Empty</span>}
                      <select className="ip-tether-sel" value={code ?? ""} onChange={(e) => setActivateCode(s.id, e.target.value)}>
                        <option value="">activate…</option>
                        {TETHERED.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
                      </select>
                    </span>
                  </div>
                );
              }
              return (
                <label key={s.id} className={`ip-rawrow ${isPicked(s.id) ? "on" : ""}`}>
                  <input type="checkbox" checked={isPicked(s.id)} onChange={() => toggle(s.id)} />
                  <span className="hh-ppt-num mono">{pptCode(s.number)}</span>
                  <span className="ip-rawname">{s.displayName}</span>
                  {(s.capturedBy ?? []).map((c) => <span key={c} className="hh-code mono">{c}</span>)}
                </label>
              );
            })}
          </div>
        ) : <div className="hh-muted ip-none">No subjects yet.</div>}
      </div>

      {/* save as — the project that falls out of these subjects; serial name + path, then Verify */}
      {totalToAdd > 0 && (
        <div className="ip-section ip-save">
          <div className="ip-section-head">
            {mode === "existing" ? "Add to project" : "Save as"} <span className="raw-hint">ingest hands you the name + path; save your project, then Verify</span>
          </div>
          <div className="ip-saveas">
            <div className="proc-pathrow">
              <span className="sa-key">File name</span>
              <span className="proc-path mono">{saveSerial}</span>
              <CopyBtn text={saveSerial} label="Copy" />
              {involvedNumbers.length > 1 && <span className="raw-hint">lowest of {involvedNumbers.length} subjects</span>}
            </div>
            <div className="proc-pathrow">
              <span className="sa-key">Location</span>
              <span className="proc-path mono">{locationPath}</span>
              <CopyBtn text={locationPath} label="Copy" />
              <span className="raw-hint">ingest made this folder</span>
            </div>
          </div>
          {involvedNumbers.length > 0 && (
            <div className="ip-manifest">
              <span className="sa-key">In project</span>
              <span className="ip-manifest-nums">
                {involvedNumbers.map((n) => <span key={n} className="hh-ppt-num mono">{pptCode(n)}</span>)}
              </span>
              <span className="raw-hint">all in the manifest</span>
            </div>
          )}
          {orderedProjects.length > 0 && (
            <div className="ip-joinrow">
              <span className="sa-key">or add to</span>
              <select className="raw-gear" value={existingId} onChange={(e) => setExistingId(e.target.value)}>
                <option value="">an existing project…</option>
                {orderedProjects.map((p) => <option key={p.id} value={p.id}>{projectFileName(nameStem, p, subjects, projects)} · {p.software}</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {err && <div className="hh-name-err">{err}</div>}

      <div className="raw-foot">
        <span className="raw-hint">{totalToAdd} {totalToAdd === 1 ? "subject" : "subjects"} ready</span>
        <button type="button" className="hh-btn primary" disabled={!canCommit} onClick={commit}>
          {busy ? "Verifying…" : "Verify"}
        </button>
      </div>
    </div>
  );
}
